if (!customElements.get('slate-logo-customizer')) {
  customElements.define(
    'slate-logo-customizer',
    class SlateLogoCustomizer extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId) || this.closest('form[action*="/cart/add"]');
        if (!this.form) return;

        this.variants = JSON.parse(this.querySelector('[data-slate-product-variants]').textContent);
        this.feeVariants = JSON.parse(this.querySelector('[data-slate-fee-variants]').textContent || '[]');
        this.currencyCode = this.dataset.currency || 'USD';
        this.quantityInput = this.querySelector('[data-slate-quantity]');
        this.details = this.querySelector('[data-slate-logo-details]');
        this.logoDetailInputs = Array.from(this.details.querySelectorAll('input, textarea, select'));
        this.fileInput = this.querySelector('[data-slate-artwork]');
        this.fileMarker = this.querySelector('[data-slate-artwork-marker]');
        this.permission = this.querySelector('[data-slate-permission]');
        this.whatsappInput = this.querySelector('[data-slate-whatsapp]');
        this.completion = this.querySelector('[data-slate-completion]');
        this.errorBox = this.querySelector('[data-slate-error]');
        this.logoFeeLabel = this.querySelector('[data-slate-logo-fee-label]');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.submitContent = this.submitButton?.querySelector('.button__content') || this.submitButton;
        this.originalSubmitText = this.submitContent?.textContent.trim() || 'Add to cart';
        this.originalSubmitAriaLabel = this.submitButton?.getAttribute('aria-label');
        this.ensureSubmitFeedbackStyles();
        this.prepareSubmitFeedback();
        this.nativeQuantityInputs = Array.from(document.querySelectorAll(`[name="quantity"][form="${CSS.escape(this.form.id)}"]`)).filter(
          (input) => input !== this.quantityInput
        );
        this.nativeQuantityInputs.forEach((input) => {
          input.removeAttribute('name');
          input.closest('.product-info__block-item')?.setAttribute('hidden', '');
        });

        this.addEventListener('change', () => this.update());
        // Keep a partially typed value in the field. MOQ normalization happens
        // on blur, change, and submit, rather than after every keystroke.
        this.quantityInput.addEventListener('input', () => this.update({ normalize: false }));
        this.quantityInput.addEventListener('blur', () => this.normalizeQuantity());
        this.whatsappInput?.addEventListener('input', () => this.update());
        this.querySelectorAll('[data-slate-quantity-change]').forEach((button) => {
          button.addEventListener('click', () => {
            const next = this.quantity + Number(button.dataset.slateQuantityChange || 0);
            this.quantityInput.value = String(Math.max(this.minimum, next));
            this.update();
          });
        });
        this.fileInput.addEventListener('change', () => {
          const file = this.fileInput.files?.[0];
          this.querySelector('[data-slate-file-label]').textContent = file ? file.name : 'Choose logo file';
          this.fileMarker.disabled = !file;
          this.update();
        });
        this.form.addEventListener('submit', (event) => this.onSubmit(event), true);
        document.addEventListener('change', (event) => {
          if (event.target.name?.startsWith('options[')) window.setTimeout(() => this.update(), 0);
        });
        this.updateFeeSchedule();
        this.update();
      }

      get minimum() { return Number(this.dataset.minimumQuantity || 20); }
      get quantity() { return Math.max(this.minimum, Number(this.quantityInput?.value || this.minimum)); }
      get logoSelected() { return this.querySelector('[name="properties[Logo Printing]"]:checked')?.value === 'Custom Logo'; }
      get placements() { return Array.from(this.querySelectorAll('[name="properties[Logo Placement]"]:checked')).map((input) => input.value); }
      get method() { return this.querySelector('[name="properties[Printing Method]"]:checked')?.value || ''; }

      selectedVariant() {
        const linkedVariantInput = this.form.id
          ? document.querySelector(`[name="id"][form="${CSS.escape(this.form.id)}"]`)
          : null;
        const id = Number(this.form.querySelector('[name="id"]')?.value || linkedVariantInput?.value);
        return this.variants.find((variant) => Number(variant.id) === id) || null;
      }

      feeForQuantity(quantity) {
        let fee;
        if (quantity <= 20) fee = { variantId: this.dataset.feeTwenty, lineQuantity: 1, cents: 7000, unitCents: 7000, unitType: 'location' };
        else if (quantity <= 50) fee = { variantId: this.dataset.feeTwentyOneToFifty, lineQuantity: 1, cents: 10000, unitCents: 10000, unitType: 'location' };
        else if (quantity <= 100) fee = { variantId: this.dataset.feeFiftyOneToHundred, lineQuantity: 1, cents: 13000, unitCents: 13000, unitType: 'location' };
        else if (quantity <= 200) fee = { variantId: this.dataset.feeOneOhOneToTwoHundred, lineQuantity: 1, cents: 17000, unitCents: 17000, unitType: 'location' };
        else if (quantity <= 500) fee = { variantId: this.dataset.feeTwoOhOneToFiveHundred, lineQuantity: quantity, cents: quantity * 95, unitCents: 95, unitType: 'box' };
        else fee = { variantId: this.dataset.feeFiveOhOnePlus, lineQuantity: quantity, cents: quantity * 80, unitCents: 80, unitType: 'box' };

        if (this.currencyCode === 'AUD' || this.currencyCode === 'GBP') {
          const marketVariant = this.feeVariants.find((variant) => Number(variant.id) === Number(fee.variantId));
          const marketUnitCents = Number(marketVariant?.price);
          if (Number.isInteger(marketUnitCents) && marketUnitCents >= 0) {
            fee.unitCents = marketUnitCents;
            fee.cents = fee.unitType === 'box' ? marketUnitCents * fee.lineQuantity : marketUnitCents;
          }
        }

        fee.label = fee.unitType === 'box'
          ? `${this.money(fee.unitCents)} per box, per location`
          : `${this.money(fee.unitCents)} per location`;
        return fee;
      }

      money(cents) {
        if (this.currencyCode === 'GBP') {
          return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
        }

        const currency = this.currencyCode === 'AUD' ? 'AUD' : 'USD';
        const locale = currency === 'AUD' ? 'en-AU' : 'en-US';
        const amount = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        return currency === 'AUD' ? `${amount} AUD` : amount;
      }

      updateFeeSchedule() {
        if (this.currencyCode !== 'AUD' && this.currencyCode !== 'GBP') return;

        this.querySelectorAll('[data-slate-fee-schedule]').forEach((cell) => {
          const marketVariant = this.feeVariants.find((variant) => Number(variant.id) === Number(cell.dataset.slateFeeSchedule));
          const marketUnitCents = Number(marketVariant?.price);
          if (!Number.isInteger(marketUnitCents) || marketUnitCents < 0) return;
          cell.textContent = `${this.money(marketUnitCents)} ${cell.dataset.slateFeeBasis}`;
        });
      }

      printingCalculation(fee, quantity, locationCount) {
        const locationLabel = `location${locationCount === 1 ? '' : 's'}`;
        const total = this.money(fee.cents * locationCount);

        if (fee.unitType === 'box') {
          return `${this.money(fee.unitCents)} × ${quantity} boxes × ${locationCount} ${locationLabel} = ${total}`;
        }

        return `${this.money(fee.unitCents)} per location × ${locationCount} ${locationLabel} = ${total}`;
      }

      printingEstimateLabel(fee, quantity, locationCount) {
        if (!locationCount) return 'Logo printing';

        const locationLabel = `location${locationCount === 1 ? '' : 's'}`;
        if (fee.unitType === 'box') {
          return `Logo printing — ${this.money(fee.unitCents)} × ${quantity} boxes × ${locationCount} ${locationLabel}`;
        }

        return `Logo printing — ${this.money(fee.unitCents)} × ${locationCount} ${locationLabel}`;
      }

      checkoutPrintingTotal(fee, quantity, locationCount) {
        const total = this.money(fee.cents * locationCount);
        if (fee.unitType === 'box') {
          return `${this.money(fee.unitCents)} per box × ${quantity} boxes × ${locationCount} locations = ${total}`;
        }
        return `${this.money(fee.unitCents)} per location × ${locationCount} = ${total}`;
      }

      boxPricingSummary(variant, quantity) {
        const unitCents = Number(variant.price);
        const lineCents = unitCents * quantity;
        return `${this.money(unitCents)} each × ${quantity} = ${this.money(lineCents)}`;
      }

      serviceQuantity(fee, quantity, locationCount) {
        const serviceUnits = fee.lineQuantity * locationCount;
        const formattedUnits = new Intl.NumberFormat('en-US').format(serviceUnits);

        if (fee.unitType === 'box') {
          return `${formattedUnits} box-location print applications — not additional boxes`;
        }

        return `${formattedUnits} print location${serviceUnits === 1 ? '' : 's'} — not additional boxes`;
      }

      wait(milliseconds) {
        return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
      }

      async fetchCart() {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const cart = await this.readCartJson(response, 'The cart could not be checked.');
        if (!response.ok) throw new Error('The cart could not be checked.');
        return cart;
      }

      async readCartJson(response, fallbackMessage) {
        const responseText = await response.text();

        try {
          return JSON.parse(responseText);
        } catch {
          // Shopify can return an HTML error or redirect page for a cart request.
          // Never expose that parser error to a customer or treat the response as a cart.
          throw new Error(fallbackMessage);
        }
      }

      cartFromBundledSections(result, cartStateSection) {
        try {
          const cartStateHtml = result?.sections?.[cartStateSection];
          const cartStateDocument = new DOMParser().parseFromString(cartStateHtml || '', 'text/html');
          const cartStateJson = cartStateDocument.querySelector('[data-slate-cart-state]')?.textContent;
          return cartStateJson ? JSON.parse(cartStateJson) : null;
        } catch {
          return null;
        }
      }

      addedGroupIsValid(cart, groupId, expectation) {
        const groupItems = (cart?.items || []).filter((item) => item.properties?._slate_logo_group === groupId);
        const bases = groupItems.filter((item) => item.properties?._slate_logo_role === 'base');
        const fees = groupItems.filter((item) => item.properties?._slate_logo_role === 'fee');
        if (bases.length !== 1 || fees.length !== 1) return false;

        const base = bases[0];
        const fee = fees[0];
        return (
          Number(base.variant_id) === Number(expectation.baseVariantId)
          && Number(base.quantity) === expectation.boxQuantity
          && Number(base.properties?._slate_logo_location_count) === expectation.locationCount
          && Number(fee.variant_id) === Number(expectation.feeVariantId)
          && Number(fee.quantity) === expectation.feeLineQuantity
          && Number(fee.properties?._slate_logo_location_count) === expectation.locationCount
        );
      }

      async addFeeWithVerification(feePayload, cartStateSection, groupId, expectation) {
        let latestError = new Error('The printing fee could not be added.');

        for (let attempt = 0; attempt < 2; attempt += 1) {
          let feeResult = null;

          try {
            const feeResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify(feePayload),
            });
            feeResult = await this.readCartJson(feeResponse, 'The logo printing charge could not be added. Please try again.');
            if (!feeResponse.ok || feeResult.status) {
              latestError = new Error(`Logo printing fee: ${feeResult.description || 'The printing fee could not be added.'}`);
            }
          } catch (error) {
            latestError = error;
          }

          let cart = this.cartFromBundledSections(feeResult, cartStateSection);
          if (!cart || !this.addedGroupIsValid(cart, groupId, expectation)) {
            try {
              cart = await this.fetchCart();
            } catch (error) {
              latestError = error;
              cart = null;
            }
          }

          if (cart && this.addedGroupIsValid(cart, groupId, expectation)) {
            cart.sections = feeResult?.sections || {};
            return cart;
          }

          const feeMayExist = cart?.items?.some(
            (item) => item.properties?._slate_logo_group === groupId && item.properties?._slate_logo_role === 'fee'
          );
          if (feeMayExist) {
            throw new Error('The logo printing charge was added incorrectly.');
          }

          if (attempt === 0) await this.wait(300);
        }

        throw latestError;
      }

      async removeLogoGroup(groupId) {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const cart = await this.fetchCart();
            const updates = {};
            cart.items.forEach((item) => {
              if (item.properties?._slate_logo_group === groupId) updates[item.key] = 0;
            });
            if (!Object.keys(updates).length) return true;

            const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ updates }),
            });
            if (!response.ok) throw new Error('The incomplete customized set could not be removed.');

            const verifiedCart = await this.fetchCart();
            if (!verifiedCart.items.some((item) => item.properties?._slate_logo_group === groupId)) return true;
          } catch {
            if (attempt < 2) await this.wait(300);
          }
        }

        return false;
      }

      normalizeQuantity() {
        if (!Number.isFinite(Number(this.quantityInput.value)) || Number(this.quantityInput.value) < this.minimum) {
          this.quantityInput.value = String(this.minimum);
        }
        this.update();
      }

      validationError() {
        if (!this.placements.length) return 'Select at least one print location.';
        if (!this.method) return 'Select a printing method.';
        const file = this.fileInput.files?.[0];
        if (!file) return 'Upload your logo artwork.';
        if (file.size > 20 * 1024 * 1024) return 'Artwork files must be 20 MB or smaller.';
        if (!this.permission.checked) return 'Confirm that you have permission to use the artwork.';
        return '';
      }

      update({ normalize = true } = {}) {
        if (normalize) this.normalizeWithoutUpdate();
        this.details.hidden = !this.logoSelected;
        this.logoDetailInputs.forEach((input) => {
          input.disabled = !this.logoSelected;
        });
        this.fileMarker.disabled = !this.logoSelected || !this.fileInput.files?.length;

        const quantity = this.quantity;
        const variant = this.selectedVariant();
        const boxCents = Number(variant?.price || 0) * quantity;
        const fee = this.feeForQuantity(quantity);
        const feeCents = fee.cents * this.placements.length;
        this.querySelector('[data-slate-box-summary]').textContent = `${quantity} boxes · ${this.money(boxCents)}`;
        this.querySelector('[data-slate-fee-tier]').textContent = `Current tier: ${fee.label}`;
        this.querySelector('[data-slate-box-subtotal]').textContent = this.money(boxCents);
        if (this.logoFeeLabel) this.logoFeeLabel.textContent = this.printingEstimateLabel(fee, quantity, this.placements.length);
        this.querySelector('[data-slate-logo-fee]').textContent = this.placements.length ? this.money(feeCents) : 'Select placement';
        this.querySelector('[data-slate-estimated-total]').textContent = this.placements.length ? this.money(boxCents + feeCents) : '—';

        if (!this.logoSelected) {
          this.completion.hidden = true;
          this.releaseSubmit();
          return;
        }

        const error = this.validationError();
        this.completion.hidden = false;
        this.completion.textContent = error
          ? 'Complete the required logo options above, then add to cart.'
          : 'Ready to add the customized box and linked printing fee to cart.';
        this.completion.classList.toggle('is-complete', !error);
        if (error) this.blockSubmit(); else this.releaseSubmit();
      }

      normalizeWithoutUpdate() {
        if (!Number.isFinite(Number(this.quantityInput.value)) || Number(this.quantityInput.value) < this.minimum) {
          this.quantityInput.value = String(this.minimum);
        }
      }

      blockSubmit() {
        if (!this.submitButton || !this.submitLabel) return;
        if (this.submitButton.dataset.slateSubmitting === 'true') return;
        const variantAvailable = this.selectedVariant()?.available !== false;
        this.submitButton.disabled = !variantAvailable;
        if (variantAvailable) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.dataset.slateIncomplete = 'true';
          this.submitLabel.textContent = this.originalSubmitText;
        } else {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitLabel.textContent = this.originalSubmitText;
        }
        delete this.submitButton.dataset.slateDisabled;
      }

      releaseSubmit() {
        if (!this.submitButton || !this.submitLabel) return;
        if (this.submitButton.dataset.slateSubmitting === 'true') return;
        const variantAvailable = this.selectedVariant()?.available !== false;
        this.submitButton.disabled = !variantAvailable;
        if (variantAvailable) this.submitButton.removeAttribute('aria-disabled');
        this.submitLabel.textContent = this.originalSubmitText;
        delete this.submitButton.dataset.slateDisabled;
        delete this.submitButton.dataset.slateIncomplete;
      }

      ensureSubmitFeedbackStyles() {
        if (document.getElementById('slate-logo-submit-feedback-styles')) return;

        const style = document.createElement('style');
        style.id = 'slate-logo-submit-feedback-styles';
        style.textContent = `
          [data-slate-submitting="true"] { cursor: progress; pointer-events: none; }
          .slate-logo-submit-content {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.7rem;
          }
          .slate-logo-submit-spinner[hidden] { display: none !important; }
          .slate-logo-submit-spinner {
            display: inline-block;
            flex: 0 0 auto;
            width: 1.1rem;
            height: 1.1rem;
            border: 2px solid currentColor;
            border-right-color: transparent;
            border-radius: 50%;
            animation: slate-logo-submit-spin 0.7s linear infinite;
            opacity: 1;
            will-change: transform;
          }
          @keyframes slate-logo-submit-spin { to { transform: rotate(360deg); } }
          @media (prefers-reduced-motion: reduce) {
            .slate-logo-submit-spinner {
              animation: none;
              border-right-color: currentColor;
              border-top-color: transparent;
              will-change: auto;
            }
          }
        `;
        document.head.appendChild(style);
      }

      prepareSubmitFeedback() {
        if (!this.submitButton || !this.submitContent) return;

        this.submitContent.classList.add('slate-logo-submit-content');
        this.submitContent.replaceChildren();

        this.submitLabel = document.createElement('span');
        this.submitLabel.className = 'slate-logo-submit-label';
        this.submitLabel.textContent = this.originalSubmitText;
        this.submitLabel.setAttribute('aria-live', 'polite');

        this.submitSpinner = document.createElement('span');
        this.submitSpinner.className = 'slate-logo-submit-spinner';
        this.submitSpinner.setAttribute('aria-hidden', 'true');
        this.submitSpinner.hidden = true;

        this.submitContent.append(this.submitLabel, this.submitSpinner);
      }

      setSubmitting(isSubmitting, { disableButton = true } = {}) {
        if (!this.submitButton || !this.submitLabel || !this.submitSpinner) return;

        if (isSubmitting) {
          if (this.submitButton.dataset.slateSubmitting === 'true') return;
          this.submitButton.dataset.slateSubmitting = 'true';
          this.submitButton.setAttribute('aria-busy', 'true');
          this.submitButton.setAttribute('aria-label', 'Adding to cart');
          this.submitLabel.textContent = 'Adding…';
          this.submitSpinner.hidden = false;

          if (disableButton) {
            this.submitButton.disabled = true;
          }
          return;
        }

        delete this.submitButton.dataset.slateSubmitting;
        this.submitButton.removeAttribute('aria-busy');
        if (this.originalSubmitAriaLabel === null) {
          this.submitButton.removeAttribute('aria-label');
        } else {
          this.submitButton.setAttribute('aria-label', this.originalSubmitAriaLabel);
        }
        this.submitLabel.textContent = this.originalSubmitText;
        this.submitSpinner.hidden = true;
        this.update();
      }

      async showSubmittingFeedback() {
        this.setSubmitting(true);
        // Let mobile browsers paint the label and spinner before the cart
        // requests begin. This avoids an apparently unresponsive first tap
        // while the linked box and printing-fee requests are pending.
        await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      }

      async addBoxOnlyToCart(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.submitButton?.dataset.slateSubmitting === 'true') return;

        const variant = this.selectedVariant();
        if (!variant || variant.available === false) return this.showError('Select an available box option.');

        this.errorBox.hidden = true;
        const formData = new FormData(this.form);
        formData.set('id', String(variant.id));
        formData.set('quantity', String(this.quantity));
        formData.set('properties[Box price]', this.boxPricingSummary(variant, this.quantity));
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);

        await this.showSubmittingFeedback();
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        let redirectToCart = false;

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const item = await this.readCartJson(response, 'The selected box could not be added. Please try again.');
          if (!response.ok || item.status) {
            throw new Error(item.description || 'The selected box could not be added.');
          }

          const cart = await this.fetchCart();
          this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: [item], cart } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
          redirectToCart = true;
        } catch (error) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          this.showError(error.message || 'The selected box could not be added. Please try again.');
        } finally {
          if (redirectToCart) {
            window.location.assign(`${window.Shopify.routes.root}cart`);
          } else {
            this.setSubmitting(false);
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          }
        }
      }

      showError(message) {
        this.errorBox.textContent = message;
        this.errorBox.hidden = false;
        this.errorBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      async onSubmit(event) {
        this.normalizeQuantity();
        if (!this.logoSelected) {
          if (this.submitButton?.dataset.slateSubmitting === 'true') {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          await this.addBoxOnlyToCart(event);
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.submitButton?.dataset.slateSubmitting === 'true') return;
        this.errorBox.hidden = true;

        const validationError = this.validationError();
        if (validationError) return this.showError(validationError);

        const quantity = this.quantity;
        const fee = this.feeForQuantity(quantity);
        const placements = this.placements;
        const locationCount = placements.length;
        const variant = this.selectedVariant();
        if (!variant || variant.available === false) return this.showError('Select an available box option.');
        const groupId = `slate-logo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        if (this.whatsappInput) this.whatsappInput.value = this.whatsappInput.value.trim();
        const formData = new FormData(this.form);
        formData.set('id', String(variant.id));
        formData.set('quantity', String(quantity));
        formData.delete('properties[Logo Placement]');
        formData.set('properties[Logo Placement]', placements.join(' + '));
        formData.set('properties[Box price]', this.boxPricingSummary(variant, quantity));
        formData.set('properties[_slate_logo_group]', groupId);
        formData.set('properties[_slate_logo_role]', 'base');
        formData.set('properties[_slate_logo_location_count]', String(locationCount));

        await this.showSubmittingFeedback();
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        let baseItem;

        try {
          const baseResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          baseItem = await this.readCartJson(baseResponse, 'The selected box could not be added. Please try again.');
          if (!baseResponse.ok || baseItem.status) {
            throw new Error(`Box option: ${baseItem.description || 'The selected box could not be added.'}`);
          }

          const sections = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
          const cartStateSection = 'slate-cart-state';
          if (!sections.includes(cartStateSection)) sections.push(cartStateSection);
          const feeCents = fee.cents * locationCount;
          const feePayload = {
            items: [{
              id: Number(fee.variantId),
              quantity: fee.lineQuantity * locationCount,
              properties: {
                'Applies to': `${quantity} boxes`,
                'Print locations': `${locationCount} — ${placements.join(' + ')}`,
                'Printing total': this.checkoutPrintingTotal(fee, quantity, locationCount),
                '_Applies to': this.dataset.productTitle,
                '_Box quantity': String(quantity),
                '_Print locations': placements.join(' + '),
                '_Printing method': this.method,
                '_Printing fee': this.money(feeCents),
                '_slate_logo_group': groupId,
                '_slate_logo_role': 'fee',
                '_slate_logo_location_count': String(locationCount),
                '_slate_logo_base_key': baseItem.key,
              },
            }],
            sections: sections.join(','),
            sections_url: window.location.pathname,
          };
          const cart = await this.addFeeWithVerification(feePayload, cartStateSection, groupId, {
            baseVariantId: variant.id,
            boxQuantity: quantity,
            locationCount,
            feeVariantId: fee.variantId,
            feeLineQuantity: fee.lineQuantity * locationCount,
          });
          this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: [baseItem], cart } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        } catch (error) {
          const cleanupSucceeded = await this.removeLogoGroup(groupId);
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          const baseMessage = error.message || 'The customized item could not be added. Please try again.';
          const cleanupMessage = cleanupSucceeded
            ? ' Nothing was added to your cart.'
            : ' Checkout has been blocked for safety. Open your cart, remove the incomplete customized set, and add it again.';
          this.showError(`${baseMessage}${cleanupMessage}`);
        } finally {
          this.setSubmitting(false);
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    }
  );
}
